import { useCallback, useEffect, useRef, useState } from "react";
import { getAiUsage } from "./aiUsageApi";

/**
 * Loads the company's AI credit balance and usage.
 *
 * @returns {{summary: object|null, loading: boolean, error: string|null, reload: Function}} Usage state with a retry.
 *
 * @remarks
 * Race-guarded with a fetch id so a slow first response cannot overwrite a
 * newer one after a retry. The panel is small, but it reports money, and a
 * stale figure winning is worse here than a spinner lasting longer.
 */
export const useAiUsage = () => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchId = useRef(0);

  const reload = useCallback(async () => {
    const id = fetchId.current + 1;
    fetchId.current = id;

    setLoading(true);

    const response = await getAiUsage();

    if (fetchId.current !== id) {
      return;
    }

    if (response?.isSuccess && response?.data?.data) {
      setSummary(response.data.data);
      setError(null);
    } else {
      setSummary(null);
      setError("Could not load your AI usage.");
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  return { summary, loading, error, reload };
};
